import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QuantityControl from "../../helper/QuantityControl";
import { CartServices } from "../../services/CartServices";

const API_BASE = "https://api.ifjona.com";

const toNumber = (val) => {
  const n = Number(String(val ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const getCurrencySymbol = (item) => {
  if (item?.resolved_price?.currency_symbol) {
    return item.resolved_price.currency_symbol;
  }

  if (item?.location?.currency?.symbol) {
    return item.location.currency.symbol;
  }

  if (item?.locationProductPrice?.location?.currency?.symbol) {
    return item.locationProductPrice.location.currency.symbol;
  }

  if (item?.productInfoLocationPrice?.location?.currency?.symbol) {
    return item.productInfoLocationPrice.location.currency.symbol;
  }

  return "$";
};

const money = (amount, currencySymbol = "$") =>
  `${currencySymbol}${Number(amount || 0).toFixed(2)}`;

const toImageUrl = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/storage/")) return `${API_BASE}${s}`;
  if (s.startsWith("storage/")) return `${API_BASE}/${s}`;
  return `${API_BASE}/storage/${s.replace(/^\/+/, "").replace(/^storage\//, "")}`;
};

const resolveImage = (item) =>
  toImageUrl((item?.product_info || item?.productInfo)?.image || item?.product?.image) ||
  "/assets/images/thumbs/product-thumb-1.png";

const resolveTitle = (item) => item?.product?.title || "Product";
const resolveDetailsUrl = (item) =>
  item?.product?.id ? `/product-details/${item.product.id}` : "/product-details";
const resolveCategory = (item) =>
  item?.product?.category?.name || item?.product?.category_name || "";

const resolveUnitPrice = (item) => {
  if (item?.resolved_price?.unit_price) {
    return toNumber(item.resolved_price.unit_price);
  }

  const productInfo = item?.product_info || item?.productInfo;
  const product = item?.product;

  if (
    item?.locationProductPrice?.flash_sales_price != null &&
    item.locationProductPrice.flash_sales_price !== ""
  ) {
    return toNumber(item.locationProductPrice.flash_sales_price);
  }

  if (productInfo?.flash_sale_price != null && productInfo.flash_sale_price !== "") {
    return toNumber(productInfo.flash_sale_price);
  }

  if (product?.flash_sale_price != null && product.flash_sale_price !== "") {
    return toNumber(product.flash_sale_price);
  }

  const override = productInfo?.current_price_override ?? null;
  if (override !== null && override !== "") {
    return toNumber(override);
  }

  if (item?.locationProductPrice?.base_current_price != null) {
    return toNumber(item.locationProductPrice.base_current_price);
  }

  return toNumber(product?.base_current_price ?? 0);
};

const hasFlashSale = (item) => {
  if (item?.resolved_price?.is_flash_sale) return true;

  if (
    item?.locationProductPrice?.flash_sales_price != null &&
    item.locationProductPrice.flash_sales_price !== ""
  ) {
    return true;
  }

  const productInfo = item?.product_info || item?.productInfo;
  const product = item?.product;

  if (productInfo?.flash_sale_price != null && productInfo.flash_sale_price !== "") return true;
  if (product?.flash_sale_price != null && product.flash_sale_price !== "") return true;

  return false;
};

const CSS_CART = `
  :root {
    --g-primary: #299e60; --g-light: #f3faf2;
    --g-border: #c4e8d1;  --g-hover: #228a52; --g-text: #1a5c3a;
    --flash-red: #dc2626; --flash-bg: #fef2f2;
  }
  .mc-cb        { accent-color:var(--g-primary); width:17px; height:17px; cursor:pointer; flex-shrink:0; }
  .mc-sel-row   { background:var(--g-light) !important; }
  .mc-ptitle    { font-size:13px; font-weight:700; color:#1d2939; text-decoration:none; line-height:1.4; }
  .mc-ptitle:hover { color:var(--g-primary); }
  .mc-cat       { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.6px; }
  .mc-xbtn      { background:none; border:none; cursor:pointer; color:#d0d5dd; padding:5px; border-radius:6px; display:flex; align-items:center; transition:color .15s,background .15s; }
  .mc-xbtn:hover { color:#d94040; background:#fff5f5; }

  .mc-flash-badge {
    display:inline-flex; align-items:center; gap:4px;
    background:var(--flash-bg); color:var(--flash-red);
    font-size:10px; font-weight:700; padding:2px 8px;
    border-radius:12px; margin-left:6px;
  }

  .mc-toolbar   { background:var(--g-light); border:1px solid var(--g-border); border-radius:8px; padding:10px 16px; margin-bottom:14px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; }
  .mc-sel-badge { background:#fff; color:var(--g-primary); border:1px solid var(--g-border); border-radius:20px; font-size:11px; font-weight:700; padding:2px 10px; }
  .mc-rem-sel   { background:#fff5f5; border:1px solid #fcc; border-radius:6px; padding:5px 12px; font-size:12px; font-weight:700; color:#d94040; cursor:pointer; }
  .mc-rem-sel:hover { background:#ffe0e0; }

  .mc-thumb   { width:64px; height:64px; object-fit:cover; border-radius:8px; }
  .mc-trow td { padding:12px 10px; border-bottom:1px solid #f5f5f5; vertical-align:middle; }
  .mc-trow:last-child td { border-bottom:none; }
  .mc-trow.mc-sel-row td { background:var(--g-light); }
  .mc-price   { font-size:13px; font-weight:700; color:#475467; white-space:nowrap; }
  .mc-price.mc-flash { color:var(--flash-red); }
  .mc-sub     { font-size:13px; font-weight:800; color:var(--g-primary); white-space:nowrap; }
  .mc-sub.mc-flash { color:var(--flash-red); }

  .mc-mcard { border:1px solid #ececec; border-radius:12px; margin-bottom:10px; overflow:hidden; }
  .mc-mcard.mc-sel-row { background:var(--g-light); border-color:var(--g-border); }
  .mc-m-top    { display:flex; align-items:flex-start; gap:12px; padding:12px 12px 8px; }
  .mc-mimg     { width:72px; height:72px; object-fit:cover; border-radius:10px; border:1px solid #ececec; flex-shrink:0; }
  .mc-m-info   { flex:1; min-width:0; }
  .mc-m-title-row { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; margin-bottom:4px; }
  .mc-m-bottom { display:flex; align-items:center; justify-content:space-between; padding:8px 12px 12px; border-top:1px solid #f5f5f5; background:var(--g-light); gap:8px; flex-wrap:wrap; }
  .mc-m-pricebox { display:flex; flex-direction:column; gap:2px; }
  .mc-m-plabel   { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.5px; }
  .mc-m-pval     { font-size:13px; font-weight:700; color:#475467; }
  .mc-m-pval.mc-flash { color:var(--flash-red); }
  .mc-m-subbox   { display:flex; flex-direction:column; align-items:flex-end; gap:2px; }
  .mc-m-sublabel { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.5px; }
  .mc-m-subval   { font-size:14px; font-weight:800; color:var(--g-primary); }
  .mc-m-subval.mc-flash { color:var(--flash-red); }

  .mc-summary {
    background:var(--g-light); border:1px solid var(--g-border);
    border-radius:10px; padding:16px 20px; margin-top:20px;
    display:flex; align-items:center; justify-content:space-between;
    flex-wrap:wrap; gap:12px;
  }
  .mc-summary-totals { display:flex; flex-direction:column; gap:4px; }
  .mc-summary-line   { display:flex; justify-content:space-between; gap:32px; font-size:13px; color:#475467; }
  .mc-summary-total-line { display:flex; justify-content:space-between; gap:32px; font-size:15px; font-weight:800; color:#111827; border-top:1px solid var(--g-border); margin-top:6px; padding-top:8px; }
  .mc-summary-val    { font-weight:700; color:var(--g-primary); font-family:'Fira Code',monospace; }
  .mc-checkout-btn {
    background:#111827; color:#fff; border:none; border-radius:9px;
    padding:11px 24px; font-size:13px; font-weight:700;
    font-family:'Plus Jakarta Sans',sans-serif;
    cursor:pointer; transition:background .15s, transform .1s;
    display:flex; align-items:center; gap:7px; white-space:nowrap;
  }
  .mc-checkout-btn:hover:not(:disabled) { background:#1f2937; transform:translateY(-1px); }
  .mc-checkout-btn:disabled { opacity:.45; cursor:default; transform:none; }

  .mc-coupon-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-top:16px; }
  .mc-coupon-input {
    flex:1; min-width:140px; padding:9px 14px;
    font-size:13px; font-family:'Plus Jakarta Sans',sans-serif;
    border:1.5px solid #e5e7eb; border-radius:9px; outline:none; color:#111827;
    transition:border-color .18s;
  }
  .mc-coupon-input:focus { border-color:#111827; }
  .mc-coupon-btn {
    padding:9px 18px; border-radius:9px; border:1.5px solid #e5e7eb;
    background:#fff; font-size:12px; font-weight:700;
    font-family:'Plus Jakarta Sans',sans-serif;
    color:#374151; cursor:pointer; transition:all .15s;
    white-space:nowrap;
  }
  .mc-coupon-btn:hover { background:#f9fafb; border-color:#d1d5db; }
`;

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const MyCart = ({ onBack }) => {
  const navigate = useNavigate();
  const cartRootRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      cartRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const [cartItems, setCartItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState("");
  const qtyTimers = useRef({});

  const loadCart = useCallback(async () => {
    setLoading(true);
    try {
      const items = await CartServices.getCart();
      const arr = Array.isArray(items) ? items : [];
      setCartItems(arr);
      setSelectedIds((prev) =>
        prev.size === 0 && arr.length > 0
          ? new Set(arr.map((i) => String(i.id)))
          : prev
      );
    } catch (err) {
      console.error("Failed to load cart:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCart();
    const evts = ["cart_updated", "customer_logged_in"];
    evts.forEach((e) => window.addEventListener(e, loadCart));
    return () => {
      evts.forEach((e) => window.removeEventListener(e, loadCart));
      Object.values(qtyTimers.current).forEach((t) => clearTimeout(t));
    };
  }, [loadCart]);

  const removeItem = async (id) => {
    try {
      await CartServices.remove(id);
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(String(id));
        return n;
      });
      await loadCart();
      window.dispatchEvent(new Event("cart_updated"));
    } catch (err) {
      console.error("Remove failed:", err);
    }
  };

  const removeSelected = async () => {
    for (const id of selectedIds) {
      try {
        await CartServices.remove(id);
      } catch {}
    }
    setSelectedIds(new Set());
    await loadCart();
    window.dispatchEvent(new Event("cart_updated"));
  };

  const setQty = useCallback(
    (id, qty) => {
      const q = Math.max(1, Number(qty || 1));
      setCartItems((prev) =>
        prev.map((x) => (String(x?.id) === String(id) ? { ...x, quantity: q } : x))
      );
      clearTimeout(qtyTimers.current[id]);
      qtyTimers.current[id] = setTimeout(async () => {
        try {
          await CartServices.update(id, q);
          window.dispatchEvent(new Event("cart_updated"));
        } catch (err) {
          console.error("Update qty failed:", err);
          await loadCart();
        }
      }, 600);
    },
    [loadCart]
  );

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(String(id)) ? n.delete(String(id)) : n.add(String(id));
      return n;
    });

  const allSelected =
    cartItems.length > 0 && cartItems.every((i) => selectedIds.has(String(i.id)));
  const someSelected = selectedIds.size > 0;
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(cartItems.map((i) => String(i.id))));

  const { subtotal, currencySymbol } = useMemo(() => {
    const selectedItems = cartItems.filter((i) => selectedIds.has(String(i.id)));
    const firstItem = selectedItems[0];
    const symbol = firstItem ? getCurrencySymbol(firstItem) : "$";

    const total = selectedItems.reduce(
      (s, it) => s + resolveUnitPrice(it) * Number(it?.quantity ?? 1),
      0
    );

    return { subtotal: total, currencySymbol: symbol };
  }, [selectedIds, cartItems]);

  return (
    <>
      <style>{CSS_CART}</style>

      <div className="ap-topbar" ref={cartRootRef}>
        <span className="ap-topbar-title">Shopping Cart</span>
        <div className="ap-topbar-right">
          <button className="ap-btn-sm outline" onClick={loadCart} disabled={loading}>
            <i className="ph ph-arrows-clockwise" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="ap-body">
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: "#6b7280",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            <i className="ph ph-arrow-left" style={{ fontSize: 15 }} />
            Back to Overview
          </button>
        </div>

        <div className="ap-section">
          <div className="ap-section-hd">
            <div>
              <div className="ap-section-title">Cart Items</div>
              <div className="ap-section-sub">
                {cartItems.length} item{cartItems.length !== 1 ? "s" : ""} in your cart
              </div>
            </div>
          </div>

          <div className="ap-section-bd">
            {loading && (
              <div className="ap-spinner">
                <div className="ap-spin-ring" />
                Loading your cart…
              </div>
            )}

            {!loading && cartItems.length === 0 && (
              <div style={{ textAlign: "center", padding: "48px 24px" }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🛒</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                  Your cart is empty
                </div>
                <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
                  Add some products to get started.
                </div>
                <Link
                  to="/shop"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 22px",
                    borderRadius: 9,
                    background: "#111827",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  <i className="ph ph-storefront" /> Go to Shop
                </Link>
              </div>
            )}

            {!loading && cartItems.length > 0 && (
              <>
                <div className="mc-toolbar">
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#344054",
                        cursor: "pointer",
                        userSelect: "none",
                        marginBottom: 0,
                      }}
                    >
                      <input type="checkbox" className="mc-cb" checked={allSelected} onChange={toggleAll} />
                      {allSelected ? "Deselect All" : "Select All"}
                    </label>
                    {someSelected && (
                      <span className="mc-sel-badge">
                        {selectedIds.size} of {cartItems.length} selected
                      </span>
                    )}
                  </div>
                  {selectedIds.size > 1 && (
                    <button className="mc-rem-sel" onClick={removeSelected}>
                      Remove Selected ({selectedIds.size})
                    </button>
                  )}
                </div>

                <div className="d-none d-md-block" style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {cartItems.map((item) => {
                        const id = String(item.id);
                        const sel = selectedIds.has(id);
                        const unitPrice = resolveUnitPrice(item);
                        const qty = Number(item?.quantity ?? 1);
                        const isFlash = hasFlashSale(item);
                        const itemCurrency = getCurrencySymbol(item);

                        return (
                          <tr key={id} className={`mc-trow${sel ? " mc-sel-row" : ""}`}>
                            <td style={{ width: 32 }}>
                              <input
                                type="checkbox"
                                className="mc-cb"
                                checked={sel}
                                onChange={() => toggleSelect(id)}
                              />
                            </td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                <Link
                                  to={resolveDetailsUrl(item)}
                                  style={{
                                    flexShrink: 0,
                                    display: "block",
                                    width: 64,
                                    height: 64,
                                    borderRadius: 8,
                                    overflow: "hidden",
                                    border: "1px solid #f0f0f0",
                                  }}
                                >
                                  <img
                                    src={resolveImage(item)}
                                    alt={resolveTitle(item)}
                                    className="mc-thumb"
                                    onError={(e) => {
                                      e.currentTarget.src = "/assets/images/thumbs/product-thumb-1.png";
                                    }}
                                  />
                                </Link>
                                <div>
                                  <div style={{ display: "flex", alignItems: "center" }}>
                                    <Link
                                      to={resolveDetailsUrl(item)}
                                      className="mc-ptitle"
                                      style={{ display: "block", marginBottom: 4 }}
                                    >
                                      {resolveTitle(item)}
                                    </Link>
                                    {isFlash && <span className="mc-flash-badge">🔥 Flash Sale</span>}
                                  </div>
                                  {resolveCategory(item) && <span className="mc-cat">{resolveCategory(item)}</span>}
                                </div>
                              </div>
                            </td>
                            <td className={`mc-price${isFlash ? " mc-flash" : ""}`}>
                              {money(unitPrice, itemCurrency)}
                            </td>
                            <td>
                              <QuantityControl initialQuantity={qty} onChange={(q) => setQty(item.id, q)} />
                            </td>
                            <td className={`mc-sub${isFlash ? " mc-flash" : ""}`}>
                              {money(unitPrice * qty, itemCurrency)}
                            </td>
                            <td style={{ width: 40 }}>
                              <button className="mc-xbtn" onClick={() => removeItem(item.id)} title="Remove">
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="d-md-none">
                  {cartItems.map((item) => {
                    const id = String(item.id);
                    const sel = selectedIds.has(id);
                    const unitPrice = resolveUnitPrice(item);
                    const qty = Number(item?.quantity ?? 1);
                    const isFlash = hasFlashSale(item);
                    const itemCurrency = getCurrencySymbol(item);

                    return (
                      <div key={id} className={`mc-mcard${sel ? " mc-sel-row" : ""}`}>
                        <div className="mc-m-top">
                          <input
                            type="checkbox"
                            className="mc-cb"
                            style={{ marginTop: 4 }}
                            checked={sel}
                            onChange={() => toggleSelect(id)}
                          />
                          <Link to={resolveDetailsUrl(item)} style={{ flexShrink: 0 }}>
                            <img
                              src={resolveImage(item)}
                              alt={resolveTitle(item)}
                              className="mc-mimg"
                              onError={(e) => {
                                e.currentTarget.src = "/assets/images/thumbs/product-thumb-1.png";
                              }}
                            />
                          </Link>
                          <div className="mc-m-info">
                            <div className="mc-m-title-row">
                              <div>
                                <Link to={resolveDetailsUrl(item)} className="mc-ptitle">
                                  {resolveTitle(item)}
                                </Link>
                                {isFlash && (
                                  <span className="mc-flash-badge" style={{ marginLeft: 0, marginTop: 4 }}>
                                    🔥 Flash Sale
                                  </span>
                                )}
                              </div>
                              <button className="mc-xbtn" onClick={() => removeItem(item.id)} title="Remove">
                                <TrashIcon />
                              </button>
                            </div>
                            {resolveCategory(item) && <span className="mc-cat">{resolveCategory(item)}</span>}
                          </div>
                        </div>
                        <div className="mc-m-bottom">
                          <div className="mc-m-pricebox">
                            <span className="mc-m-plabel">Unit Price</span>
                            <span className={`mc-m-pval${isFlash ? " mc-flash" : ""}`}>
                              {money(unitPrice, itemCurrency)}
                            </span>
                          </div>
                          <QuantityControl initialQuantity={qty} onChange={(q) => setQty(item.id, q)} />
                          <div className="mc-m-subbox">
                            <span className="mc-m-sublabel">Subtotal</span>
                            <span className={`mc-m-subval${isFlash ? " mc-flash" : ""}`}>
                              {money(unitPrice * qty, itemCurrency)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mc-coupon-row">
                  <input
                    type="text"
                    className="mc-coupon-input"
                    placeholder="Coupon code"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value)}
                  />
                  <button type="button" className="mc-coupon-btn">
                    Apply Coupon
                  </button>
                </div>

                <div className="mc-summary">
                  <div className="mc-summary-totals">
                    <div className="mc-summary-line">
                      <span>Subtotal ({selectedIds.size} items)</span>
                      <span className="mc-summary-val">{money(subtotal, currencySymbol)}</span>
                    </div>
                    <div className="mc-summary-line">
                      <span>Estimated Delivery</span>
                      <span style={{ fontWeight: 700, color: "#16a34a" }}>Free</span>
                    </div>
                    <div className="mc-summary-line">
                      <span>Estimated Tax</span>
                      <span className="mc-summary-val">{money(0, currencySymbol)}</span>
                    </div>
                    <div className="mc-summary-total-line">
                      <span>Total</span>
                      <span className="mc-summary-val">{money(subtotal, currencySymbol)}</span>
                    </div>
                  </div>

                  <button
                    className="mc-checkout-btn"
                    disabled={!someSelected}
                    onClick={() =>
                      navigate("/checkout", {
                        state: { selectedIds: Array.from(selectedIds) },
                      })
                    }
                  >
                    <i className="ph ph-lock-simple" style={{ fontSize: 14 }} />
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyCart;